
import { useState, useRef, useEffect } from 'react';

interface BackupSolution {
  title: string;
  description: string;
  icon: string;
  features: string[];
  rto: string;
  rpo: string;
  color: string;
}

export default function DataBackup() {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const backupSolutions: BackupSolution[] = [
    {
      title: "Cloud Backup",
      description: "Secure, scalable cloud-based backup solutions with global redundancy",
      icon: "ri-cloud-line",
      features: ["Automated Backups", "Global Redundancy", "Encryption at Rest", "Incremental Backups", "Cross-Region Replication"],
      rto: "< 4 Hours",
      rpo: "< 15 Minutes",
      color: "from-blue-600 to-cyan-500"
    },
    {
      title: "Hybrid Backup",
      description: "Combined on-premises and cloud backup for optimal performance and security",
      icon: "ri-server-line",
      features: ["Local Fast Recovery", "Cloud Long-term Storage", "Bandwidth Optimization", "Flexible Scheduling", "Compliance Ready"],
      rto: "< 1 Hour",
      rpo: "< 5 Minutes",
      color: "from-purple-600 to-pink-500"
    },
    {
      title: "Disaster Recovery",
      description: "Complete business continuity planning with automated failover capabilities",
      icon: "ri-refresh-line",
      features: ["Automated Failover", "Business Continuity", "Recovery Testing", "Runbook Automation", "Priority Recovery"],
      rto: "< 30 Minutes",
      rpo: "< 1 Minute",
      color: "from-green-600 to-teal-500"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleItems(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const items = sectionRef.current?.querySelectorAll('.backup-item');
    items?.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              Data Backup & Disaster Recovery
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive data protection and business continuity solutions to safeguard your critical information
          </p>
        </div>

        {/* 3D Backup Visualization */}
        <div className="mb-16 text-center">
          <div className="relative inline-block group">
            <img 
              src="https://readdy.ai/api/search-image?query=3D%20rendered%20data%20backup%20and%20disaster%20recovery%20infrastructure%20showing%20servers%2C%20cloud%20storage%2C%20data%20replication%20flows%2C%20holographic%20backup%20processes%2C%20network%20connections%20with%20glowing%20data%20streams%2C%20professional%20data%20center%20visualization%20with%20green%20and%20blue%20accents&width=1200&height=600&seq=backup3d&orientation=landscape"
              alt="3D Backup Infrastructure"
              className="max-w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-all duration-700 cursor-pointer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-teal-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>

        {/* Backup Solution Tabs */}
        <div className="mb-16">
          <div className="flex flex-col sm:flex-row justify-center mb-8 space-y-4 sm:space-y-0 sm:space-x-4">
            {backupSolutions.map((solution, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 ${
                  activeTab === index
                    ? `bg-gradient-to-r ${solution.color} text-white shadow-lg`
                    : 'bg-slate-800/60 text-gray-300 hover:bg-slate-700/60 border border-slate-600'
                }`}
              >
                <i className={`${solution.icon} mr-2`}></i>
                {solution.title}
              </button>
            ))}
          </div>

          {/* Active Solution Details */}
          <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${backupSolutions[activeTab].color} rounded-xl flex items-center justify-center mr-4`}>
                    <i className={`${backupSolutions[activeTab].icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white">
                    {backupSolutions[activeTab].title}
                  </h3>
                </div>
                
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  {backupSolutions[activeTab].description}
                </p>

                {/* RTO/RPO Metrics */}
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="text-center p-4 bg-slate-700/40 rounded-xl border border-slate-600/50">
                    <div className="text-2xl font-bold text-emerald-400 mb-1">
                      {backupSolutions[activeTab].rto}
                    </div>
                    <div className="text-gray-400 text-sm">Recovery Time Objective</div>
                  </div>
                  <div className="text-center p-4 bg-slate-700/40 rounded-xl border border-slate-600/50">
                    <div className="text-2xl font-bold text-teal-400 mb-1">
                      {backupSolutions[activeTab].rpo}
                    </div>
                    <div className="text-gray-400 text-sm">Recovery Point Objective</div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3">
                  {backupSolutions[activeTab].features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex}
                      className="flex items-center p-3 bg-slate-700/40 rounded-lg border border-slate-600/50 hover:border-emerald-400/50 transition-colors duration-300"
                    >
                      <i className="ri-check-line text-emerald-400 mr-3"></i>
                      <span className="text-gray-200">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <img 
                  src={`https://readdy.ai/api/search-image?query=Professional%20$%7BbackupSolutions%5BactiveTab%5D.title.toLowerCase%28%29%7D%20solution%20dashboard%20showing%20data%20backup%20progress%2C%20recovery%20metrics%2C%20storage%20utilization%2C%20backup%20schedules%2C%20and%20system%20health%20monitoring%20with%20modern%20cybersecurity%20interface%20design&width=600&height=500&seq=backup${activeTab}&orientation=landscape`}
                  alt={backupSolutions[activeTab].title}
                  className="w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-transform duration-700"
                />
                
                {/* Animated status indicator */}
                <div className={`absolute top-4 right-4 w-4 h-4 bg-gradient-to-r ${backupSolutions[activeTab].color} rounded-full animate-pulse`}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Recovery Process Steps */}
        <div className="mb-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-center text-white mb-12">
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              Disaster Recovery Process
            </span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: "1", title: "Detection", desc: "Automated monitoring detects issues", icon: "ri-alarm-warning-line" },
              { step: "2", title: "Assessment", desc: "Evaluate impact and scope", icon: "ri-search-line" },
              { step: "3", title: "Activation", desc: "Trigger recovery procedures", icon: "ri-play-line" },
              { step: "4", title: "Recovery", desc: "Restore systems and data", icon: "ri-refresh-line" },
              { step: "5", title: "Validation", desc: "Verify system integrity", icon: "ri-check-line" }
            ].map((step, index) => (
              <div
                key={index}
                data-index={index + 20}
                className={`backup-item text-center group relative ${
                  visibleItems.includes(index + 20) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                } transition-all duration-500`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-800/60 transform transition-all duration-300 hover:scale-105 cursor-pointer">
                  <div className="relative mb-4">
                    <div className={`w-16 h-16 mx-auto bg-gradient-to-r from-emerald-600 to-teal-500 rounded-full flex items-center justify-center group-hover:animate-pulse`}>
                      <i className={`${step.icon} text-xl text-white`}></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {step.step}
                    </div>
                  </div>
                  
                  <h4 className="font-semibold text-white mb-2 group-hover:text-emerald-300 transition-colors duration-300">
                    {step.title}
                  </h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                </div>

                {/* Connection line */}
                {index < 4 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-emerald-500/50 to-teal-500/50 z-0"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Backup Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { stat: "99.99%", label: "Data Availability", icon: "ri-database-line", color: "from-emerald-500 to-teal-400" },
            { stat: "< 15min", label: "RPO Guarantee", icon: "ri-time-line", color: "from-blue-500 to-cyan-400" },
            { stat: "24/7", label: "Monitoring", icon: "ri-eye-line", color: "from-purple-500 to-pink-400" },
            { stat: "256-bit", label: "Encryption", icon: "ri-lock-line", color: "from-orange-500 to-red-400" }
          ].map((stat, index) => (
            <div
              key={index}
              data-index={index + 30}
              className={`backup-item text-center p-6 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl hover:bg-slate-800/60 transform transition-all duration-500 hover:scale-105 cursor-pointer ${
                visibleItems.includes(index + 30) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${(index + 10) * 100}ms` }}
            >
              <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center`}>
                <i className={`${stat.icon} text-2xl text-white`}></i>
              </div>
              <div className="text-3xl font-bold text-white mb-2">{stat.stat}</div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
